import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import {
  ListItem,
  ListItemText,
  Typography,
  Autocomplete,
  TextField,
  LinearProgress,
  Box,
} from '@mui/material';
import moment from 'moment';

import { useVenues } from '../providers/venues';
import {
  TaskEventSearchQuery,
  TaskEventSearchQueryVariables,
} from '@pv/common/graphql/operations';

interface Event {
  id: string;
  startDate: string;
  startOffset: number;
  name: string;
  planner: {
    id: string;
    fullName: string;
  };
}

const eventSearchQuery = gql`
  query TaskEventSearch(
    $venueIds: [ID!]!
    $query: String
    $first: Int
    $after: String
  ) {
    eventSearch(
      venueIds: $venueIds
      query: $query
      first: $first
      after: $after
    ) {
      nodes {
        id
        name
        startDate
        startOffset
        endDate
        endOffset
        planner {
          id
          fullName
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

interface Props {
  event?: Event;
  onSelectEvent: (event: Event) => void;
}

const renderDate = (event: Event) => {
  if (event.startDate) {
    return (
      <span>
        (
        {moment(event.startDate)
          .startOf('day')
          .add(event.startOffset || 0, 'minutes')
          .format('MMM D, YYYY')}
        )
      </span>
    );
  }
  return null;
};

export const EventSearch = ({ event, onSelectEvent }: Props) => {
  const [query, setQuery] = useState<string>('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { venues, venueIds } = useVenues();
  const variables: TaskEventSearchQueryVariables = {
    venueIds,
    query: query || undefined,
    first: 20,
  };
  const skip = !venues || !query;
  const { data, loading, fetchMore } = useQuery<
    TaskEventSearchQuery,
    TaskEventSearchQueryVariables
  >(eventSearchQuery, {
    variables,
    skip,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const results = (data?.eventSearch?.nodes || []).filter(
    (event): event is Event => event != null
  );

  // Add a loading indicator option when loading more
  const optionsWithLoading = useMemo(() => {
    if (isLoadingMore) {
      return [...results, { id: '__loading__', name: '__loading__' } as Event];
    }
    return results;
  }, [results, isLoadingMore]);

  const pageInfo = data?.eventSearch?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage ?? false;
  const endCursor = pageInfo?.endCursor;

  const loadMore = useCallback(async () => {
    if (!hasNextPage || loading || isLoadingMore || !endCursor || !query)
      return;

    setIsLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          after: endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.eventSearch) return prev;

          return {
            eventSearch: {
              ...fetchMoreResult.eventSearch,
              nodes: [
                ...(prev.eventSearch?.nodes || []),
                ...(fetchMoreResult.eventSearch.nodes || []),
              ],
            },
          };
        },
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasNextPage, loading, isLoadingMore, endCursor, fetchMore, query]);

  const handleListboxScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      const target = event.currentTarget;
      const scrollBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      // Load more when within 100px of the bottom
      if (scrollBottom < 100 && hasNextPage && !loading && !isLoadingMore) {
        loadMore();
      }
    },
    [hasNextPage, loading, isLoadingMore, loadMore]
  );

  // Reset loading state when query changes
  useEffect(() => {
    setIsLoadingMore(false);
  }, [query]);

  return (
    <Autocomplete
      options={optionsWithLoading}
      value={event}
      inputValue={query}
      loading={loading && results.length === 0}
      onInputChange={(_, newInputValue) => setQuery(newInputValue)}
      onChange={(_, event) => {
        // Don't allow selection of the loading indicator
        if (event && event.id !== '__loading__') {
          onSelectEvent(event);
        }
      }}
      getOptionLabel={(option) => {
        if (option.id === '__loading__') return '';
        return (
          option.name || option.planner?.fullName || option.startDate || ''
        );
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(options) => options} // Don't filter, we handle it ourselves
      ListboxProps={{
        onScroll: handleListboxScroll,
      }}
      renderOption={(props, event) => {
        // Render loading indicator for the special loading option
        if (event.id === '__loading__') {
          // Don't spread props for the loading indicator - it's not a selectable option
          return (
            <Box
              key="__loading__"
              component="li"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: (theme) => theme.spacing(1),
                listStyle: 'none',
              }}
            >
              <LinearProgress sx={{ width: '100%' }} />
            </Box>
          );
        }

        return (
          <ListItem
            className="event-search-result"
            onClick={() => onSelectEvent(event)}
            {...props}
          >
            <ListItemText>
              <div>{event.planner?.fullName}</div>
              <Typography color="textSecondary">
                {event.name} {renderDate(event)}
              </Typography>
            </ListItemText>
          </ListItem>
        );
      }}
      renderInput={(params) => <TextField {...params} required label="Event" />}
    />
  );
};
